import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { motion } from "motion/react";

// Utility for image compression
const compressImage = (file, maxWidth, maxHeight, quality = 0.8) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > maxWidth) {
                        height = Math.round((height * maxWidth) / width);
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = Math.round((width * maxHeight) / height);
                        height = maxHeight;
                    }
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob((blob) => {
                    blob ? resolve(blob) : reject(new Error('Error en conversión WebP'));
                }, 'image/webp', quality);
            };
        };
        reader.onerror = (error) => reject(error);
    });
};

const CATEGORIES = [
    "Alimentos y Bebidas",
    "Clínica / Sanatorio",
    "Deportes y Gimnasio",
    "Educación / Librería",
    "Especialidades Médicas",
    "Estética / Peluquería",
    "Estudios / Laboratorio",
    "Farmacia",
    "Gastronomía / Restaurantes",
    "Hogar / Decoración",
    "Indumentaria / Calzado",
    "Odontología",
    "Óptica",
    "Otro / Servicios",
    "Supermercado",
    "Turismo / Hotelería"
].sort();

const PROVINCES = [
    "CABA", "Buenos Aires", "Catamarca", "Chaco", "Chubut", "Córdoba", "Corrientes",
    "Entre Ríos", "Formosa", "Jujuy", "La Pampa", "La Rioja", "Mendoza", "Misiones",
    "Neuquén", "Río Negro", "Salta", "San Juan", "San Luis", "Santa Cruz",
    "Tierra del Fuego", "Tucumán"
];

const SupasoLogoAnimated = ({ size = "normal" }) => {
    const isSmall = size === "small";
    const scale = isSmall ? 0.35 : 1;
    return (
        <div className={`relative flex items-center justify-center ${isSmall ? '' : 'mb-6'}`}>
            <div className="relative flex items-center justify-center" style={{ transform: `scale(${scale})` }}>
                <motion.div
                    className="absolute w-[103px] h-[103px] border-[7px] border-transparent border-t-[#00A400] border-r-[#00A400] border-b-[#00A400] rounded-full"
                    animate={{ rotate: -360 }}
                    transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                />
                <motion.div
                    className="absolute w-[86px] h-[86px] border-[7px] border-transparent border-t-[#FD3033] border-l-[#FD3033] border-b-[#FD3033] rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                />
                <div className="relative z-10 flex items-center justify-center bg-white rounded-full p-1 shadow-sm">
                    <img src="https://supaso.org/logo.png" alt="Logo" className="w-[67px] h-[67px] object-contain" referrerPolicy="no-referrer" />
                </div>
            </div>
        </div>
    );
};

export default function PrestadoresSolicitud() {
    const [screen, setScreen] = useState('form'); // 'form' or 'success'
    const [loading, setLoading] = useState(false);
    const [geoLoading, setGeoLoading] = useState(false);

    // Multimedia state
    const [logo, setLogo] = useState({ file: null, prev: null });
    const [gallery, setGallery] = useState([
        { file: null, prev: null },
        { file: null, prev: null },
        { file: null, prev: null },
        { file: null, prev: null }
    ]);

    // Geolocation state
    const [coords, setCoords] = useState({ lat: '', lon: '' });

    const handleFile = async (e, index = -1) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const max = index === -1 ? 500 : 1200;
            const compressed = await compressImage(file, max, max);
            const url = URL.createObjectURL(compressed);

            if (index === -1) {
                setLogo({ file: compressed, prev: url });
            } else {
                const newGallery = [...gallery];
                newGallery[index] = { file: compressed, prev: url };
                setGallery(newGallery);
            }
        } catch (err) {
            console.error("Error al procesar imagen:", err);
        }
    };

    const searchAddress = async (e) => {
        e.preventDefault();
        const address = document.getElementById('direccion').value;
        const city = document.getElementById('localidad').value;
        const prov = document.getElementById('provincia').value;

        if (!address || !city) {
            alert("Por favor ingresa Dirección y Localidad para buscar coordenadas.");
            return;
        }

        setGeoLoading(true);
        try {
            const query = `${address}, ${city}, ${prov}, Argentina`;
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`);
            const data = await res.json();

            if (data && data.length > 0) {
                const { lat, lon } = data[0];
                setCoords({ lat, lon });
            } else {
                alert("No se encontraron coordenadas para esa dirección. Intenta ser más específico.");
            }
        } catch (err) {
            console.error(err);
        } finally {
            setGeoLoading(false);
        }
    };

    const validateCUIT = (cuit) => {
        const clean = cuit.replace(/\D/g, '');
        if (clean.length !== 11) return false;
        const [a, b, c, d, e, f, g, h, i, j, k] = clean.split('').map(Number);
        const multi = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
        const sum = [a, b, c, d, e, f, g, h, i, j].reduce((acc, val, idx) => acc + (val * multi[idx]), 0);
        const res = 11 - (sum % 11);
        const check = res === 11 ? 0 : (res === 10 ? 9 : res);
        return check === k;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const fd = new FormData(e.target);

        try {
            // 0. Validations
            const cuit = fd.get('cuit');
            if (cuit && !validateCUIT(cuit)) {
                alert("❌ El CUIT ingresado no es válido. Por favor verifícalo.");
                setLoading(false);
                return;
            }

            const whatsapp = fd.get('tel').replace(/\D/g, '');
            if (whatsapp.length < 10) {
                alert("❌ El número de WhatsApp/Celular debe tener al menos 10 dígitos (ej: 1122334455).");
                setLoading(false);
                return;
            }

            // 1. Insert base data
            const payload = {
                nombre_fantasia: fd.get('nombre').toUpperCase(),
                razon_social: fd.get('razon').toUpperCase(),
                cuit: fd.get('cuit'),
                rubro: fd.get('rubro'),
                beneficios: fd.get('beneficios'),
                provincia: fd.get('provincia'),
                localidad: fd.get('localidad').toUpperCase(),
                direccion: fd.get('direccion').toUpperCase(),
                latitud: coords.lat ? parseFloat(coords.lat) : null,
                longitud: coords.lon ? parseFloat(coords.lon) : null,
                email_contacto: fd.get('email_contacto')?.toLowerCase(),
                telefono: fd.get('tel'),
                telefono_fijo: fd.get('tel_fijo'), // Nuevo campo
                contacto: fd.get('contacto').toUpperCase(),
                web: fd.get('web'),
                instagram: fd.get('instagram'),
                vigencia_inicio: fd.get('vigencia_inicio') || null,
                vigencia_fin: fd.get('vigencia_fin') || null,
                url_terminos: fd.get('url_terminos'),
                referido_por: fd.get('referido_por')?.toUpperCase(),
                movil_de_referente: fd.get('movil_referente'),
                descripcion_comercial: fd.get('descripcion_comercial'),
                etiquetas: (fd.get('etiquetas') || '').split(',').map(t => t.trim().toLowerCase()).filter(t => t),
                estado: 'pendiente'
            };

            const { data, error } = await supabase.from('prestadores').insert([payload]).select();
            if (error) throw error;
            const newId = data[0].id;

            // 2. Upload Multimedia
            const uploads = [];

            // Upload Logo
            if (logo.file) {
                uploads.push(uploadFile(newId, 'logo', logo.file, 'logo_url'));
            }

            // Upload Gallery
            const galleryBlobs = gallery.filter(g => g.file).map(g => g.file);
            if (galleryBlobs.length > 0) {
                uploads.push(uploadGallery(newId, galleryBlobs));
            }

            await Promise.all(uploads);
            setScreen('success');
        } catch (err) {
            alert("Error al enviar solicitud: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const uploadFile = async (id, type, blob, column) => {
        const path = `prestadores/${id}/${type}-${Date.now()}.webp`;
        const { error } = await supabase.storage.from('prestadores_multimedia').upload(path, blob);
        if (!error) {
            const { data: { publicUrl } } = supabase.storage.from('prestadores_multimedia').getPublicUrl(path);
            await supabase.from('prestadores').update({ [column]: publicUrl }).eq('id', id);
        }
    };

    const uploadGallery = async (id, blobs) => {
        const urls = [];
        for (let i = 0; i < blobs.length; i++) {
            const path = `prestadores/${id}/galeria-${Reflect.ownKeys(urls).length}-${Date.now()}.webp`;
            const { error } = await supabase.storage.from('prestadores_multimedia').upload(path, blobs[i]);
            if (!error) {
                const { data: { publicUrl } } = supabase.storage.from('prestadores_multimedia').getPublicUrl(path);
                urls.push(publicUrl);
            }
        }
        if (urls.length > 0) {
            await supabase.from('prestadores').update({ galeria_urls: urls }).eq('id', id);
        }
    };

    if (screen === 'success') return (
        <div className="min-h-screen flex items-center justify-center bg-[#f8fafc] p-6 text-center font-sans">
            <div className="bg-white p-10 rounded-[2rem] shadow-2xl max-w-md w-full border border-blue-50">
                <div className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6 text-5xl">
                    <span className="material-icons text-5xl">check_circle</span>
                </div>
                <h2 className="text-3xl font-black text-blue-950 mb-4 uppercase tracking-tight">¡Solicitud Enviada!</h2>
                <p className="text-slate-500 mb-8 leading-relaxed">Hemos recibido tu propuesta. Nuestro equipo de auditoría validará los datos y nos pondremos en contacto a la brevedad.</p>
                <button onClick={() => window.location.reload()} className="w-full bg-[#0086CE] hover:bg-blue-700 text-white py-4 rounded-2xl font-black uppercase tracking-widest transition-all transform active:scale-95 shadow-lg shadow-blue-200">Finalizar</button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-50 py-12 px-4 flex justify-center font-sans">
            <div className="max-w-4xl w-full space-y-8">

                {/* Header Minimalista */}
                <div className="text-center space-y-3 relative">
                    <SupasoLogoAnimated />
                    <h1 className="text-3xl md:text-4xl font-black text-slate-800 uppercase tracking-tighter">
                        Red de Beneficios
                    </h1>
                    <p className="text-slate-500 font-medium tracking-wide max-w-lg mx-auto">
                        Súmate como prestador oficial y llega a miles de profesionales en todo el país.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="bg-white rounded-[2.5rem] shadow-xl overflow-hidden border border-slate-100">
                    <div className="p-8 md:p-12 space-y-12">

                        {/* SECCIÓN 1: Identidad Visual */}
                        <section className="space-y-6">
                            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                                <span className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xs">01</span>
                                <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight">Identidad Visual</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                                <div className="md:col-span-1 flex flex-col items-center gap-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Logo Oficial</label>
                                    <div className="w-32 h-32 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden cursor-pointer hover:border-blue-400 transition-all group" onClick={() => document.getElementById('logo_in').click()}>
                                        {logo.prev ? <img src={logo.prev} className="w-full h-full object-cover" /> : <span className="material-icons text-slate-300 text-4xl group-hover:text-blue-400">add_business</span>}
                                    </div>
                                    <input type="file" id="logo_in" hidden accept="image/*" onChange={(e) => handleFile(e)} />
                                </div>

                                <div className="md:col-span-4 space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center md:text-left block">Galería del Local / Productos (MÁX 4)</label>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        {gallery.map((img, i) => (
                                            <div key={i} className="aspect-square bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden cursor-pointer hover:border-blue-400 transition-all group" onClick={() => document.getElementById(`gal_${i}`).click()}>
                                                {img.prev ? <img src={img.prev} className="w-full h-full object-cover" /> : <span className="material-icons text-slate-300 text-2xl group-hover:text-blue-400 text-center">add_a_photo</span>}
                                                <input type="file" id={`gal_${i}`} hidden accept="image/*" onChange={(e) => handleFile(e, i)} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* SECCIÓN 2: Datos del Comercio */}
                        <section className="space-y-6">
                            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                                <span className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xs">02</span>
                                <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight">Información Comercial</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Nombre de Fantasía</label>
                                    <input name="nombre" required placeholder="Cómo lo conocen tus clientes" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700" />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Persona de Contacto / Responsable</label>
                                    <input name="contacto" required placeholder="Nombre del responsable comercial" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700" />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Razón Social</label>
                                    <input name="razon" required placeholder="Nombre legal de la empresa" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700" />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-400 uppercase ml-1">CUIT</label>
                                    <input name="cuit" placeholder="XX-XXXXXXXX-X" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700" />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Rubro Principal</label>
                                    <div className="relative">
                                        <select name="rubro" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700 appearance-none">
                                            <option value="">Seleccionar Rubro...</option>
                                            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                        <span className="material-icons absolute right-4 top-4 text-slate-400 pointer-events-none">expand_more</span>
                                    </div>
                                </div>
                                <div className="md:col-span-2 space-y-1.5">
                                    <label className="text-[10px] font-[900] text-blue-600 dark:text-blue-400 uppercase ml-1 flex items-center gap-1">
                                        <span className="material-icons text-[14px]">storefront</span>
                                        ¿Qué vende / hace / ofrece? (Descripción Comercial)
                                    </label>
                                    <textarea name="descripcion_comercial" rows="2" placeholder="Ej: Vuelo en parapente, clases de surf, excursiones en 4x4..." className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700 resize-none"></textarea>
                                </div>
                                <div className="md:col-span-2 space-y-1.5">
                                    <label className="text-[10px] font-[900] text-blue-600 dark:text-blue-400 uppercase ml-1 flex items-center gap-1">
                                        <span className="material-icons text-[14px]">sell</span>
                                        Etiquetas / Filtros de búsqueda (Separadas por coma)
                                    </label>
                                    <input name="etiquetas" placeholder="Ej: camping, excursión, aventura, caballos" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700" />
                                </div>
                                <div className="md:col-span-2 space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Detalle del Beneficio</label>
                                    <textarea name="beneficios" rows="3" required placeholder="Ej: 20% de descuento abonando en efectivo sobre el total de la compra." className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700 resize-none"></textarea>
                                </div>
                            </div>
                        </section>

                        {/* SECCIÓN 3: Ubicación Geográfica */}
                        <section className="space-y-6">
                            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                                <span className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xs">03</span>
                                <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight">Ubicación y Contacto</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Provincia</label>
                                    <div className="relative">
                                        <select id="provincia" name="provincia" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700 appearance-none">
                                            {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                        <span className="material-icons absolute right-4 top-4 text-slate-400 pointer-events-none">expand_more</span>
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Localidad / Ciudad</label>
                                    <input id="localidad" name="localidad" required placeholder="Nombre de la ciudad" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700" />
                                </div>
                                <div className="md:col-span-2 space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Dirección Exacta</label>
                                    <div className="flex gap-2">
                                        <input id="direccion" name="direccion" required placeholder="Calle, número, entre calles..." className="flex-1 p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700" />
                                        <button type="button" onClick={searchAddress} disabled={geoLoading} className="bg-slate-800 hover:bg-black text-white px-6 rounded-2xl font-bold text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 group transition-all">
                                            {geoLoading ? <div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent"></div> : <span className="material-icons text-sm group-hover:scale-125 transition-transform">my_location</span>}
                                            {geoLoading ? '...' : 'Verificar'}
                                        </button>
                                    </div>
                                </div>

                                {coords.lat && (
                                    <div className="md:col-span-2 p-4 bg-blue-50 rounded-2xl border border-blue-100 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <span className="material-icons text-blue-500">fmd_good</span>
                                            <div>
                                                <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest leading-none">Coordenadas Verificadas</p>
                                                <p className="text-xs font-mono text-blue-700 font-bold mt-1">{coords.lat}, {coords.lon}</p>
                                            </div>
                                        </div>
                                        <button type="button" onClick={() => setCoords({ lat: '', lon: '' })} className="text-blue-400 hover:text-red-500 material-icons">cancel</button>
                                    </div>
                                )}

                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-400 uppercase ml-1">WhatsApp / Celular (Obligatorio)</label>
                                    <input name="tel" required placeholder="Ej: 1122334455" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700" />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Teléfono Fijo / 0810 (Opcional)</label>
                                    <input name="tel_fijo" placeholder="Ej: 08103334444" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700" />
                                </div>
                                <div className="space-y-1.5 md:col-span-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Email de Contacto</label>
                                    <input name="email_contacto" type="email" placeholder="email@contacto.com" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700" />
                                </div>
                            </div>
                        </section>

                        {/* SECCIÓN 4: Información de Referencia (Sutil) */}
                        <section className="p-8 bg-slate-50 rounded-[2rem] border border-slate-100 relative overflow-hidden group transition-all hover:bg-slate-100/50">
                            <div className="relative z-10 space-y-6">
                                <h3 className="text-md font-black text-slate-700 uppercase tracking-tight flex items-center gap-2">
                                    <span className="material-icons text-blue-500">handshake</span>
                                    Información de Referencia (Opcional)
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black text-slate-400 uppercase ml-1">¿Quién lo refirió?</label>
                                        <input name="referido_por" placeholder="Nombre del referente" className="w-full p-4 bg-white rounded-2xl border border-slate-200 outline-none focus:border-blue-500 transition-all font-medium text-slate-700" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Móvil del Referente</label>
                                        <input name="movil_referente" placeholder="Teléfono del contacto" className="w-full p-4 bg-white rounded-2xl border border-slate-200 outline-none focus:border-blue-500 transition-all font-medium text-slate-700" />
                                    </div>
                                </div>
                            </div>
                        </section>

                        <div className="pt-4">
                            <button disabled={loading} className="w-full bg-[#0086CE] hover:bg-blue-600 text-white py-6 rounded-[1.5rem] font-black uppercase tracking-[0.2em] shadow-xl shadow-blue-100 transition-all transform active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-4">
                                {loading && <SupasoLogoAnimated size="small" />}
                                {loading ? 'PROCESANDO SOLICITUD...' : 'ENVIAR SOLICITUD DE ALTA'}
                            </button>
                            <p className="text-center text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-8">
                                Al enviar esta solicitud, acepta los términos y condiciones de la Red de Beneficios SUPASO.
                            </p>
                        </div>

                    </div>
                </form>

                {/* Footer corporativo interno */}
                <div className="w-full py-12 text-center">
                    <p style={{ fontSize: '9px', color: '#00A400', fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em' }} className="font-medium">
                        © {new Date().getFullYear()} - <span style={{ fontFamily: '"Russo One", sans-serif', fontWeight: 500, color: '#0086CE' }}>SUPASO</span> <span style={{ color: '#0086CE', fontWeight: 700 }}>Tecnología</span> - v2.8
                    </p>
                </div>
            </div >
        </div >
    );
}
